<mat-dialog-actions class="amendment-header">
  <div class="title-group">
    <app-x-icon-button
      title="Fermer"
      data-tour-anchor="amendment.close"
      (click)="close()"
    ></app-x-icon-button>
    <span class="title">{{ title() }}</span>
    @if (link(); as link) {
      <app-copy-location-button [url]="link"></app-copy-location-button>
    }
    <app-contract-status
      [small]="true"
      [status]="amendment()?.status ?? 'DRAFT'"
    ></app-contract-status>
    @if (amendment(); as amendment) {
      <app-signature-send-failure [contract]="amendment"></app-signature-send-failure>
    }
    <span class="contract-signed">{{
      contractSigned ? 'Contrat signé' : 'Contrat pas encore signé'
    }}</span>
    @if (consultation()) {
      <mat-button-toggle-group
        class="consultation-mode"
        data-tour-anchor="amendment.consultation-mode"
        aria-label="Mode de consultation"
        [value]="mode()"
        (change)="mode.set($event.value)"
      >
        <mat-button-toggle value="changes">Modifications</mat-button-toggle>
        <mat-button-toggle value="complete">Complet</mat-button-toggle>
      </mat-button-toggle-group>
    }
  </div>
  <div class="actions">
    @if (amendment(); as amendment) {
      <app-amendment-action-buttons
        [amendment]="amendment"
        [contractSigned]="contractSigned"
        (documentReady)="openDocument($event)"
      ></app-amendment-action-buttons>
    }
    @if (!readOnly()) {
      <app-save-button
        data-tour-anchor="amendment.save"
        (click)="save()"
        [isEditMode]="!!amendment()"
        [enabled]="canSave()"
      ></app-save-button>
    }
  </div>
</mat-dialog-actions>

<mat-dialog-content>
  @if (refusal(); as refusal) {
    <div class="refusal" role="alert">
      <strong>Cet avenant ne peut pas être enregistré.</strong>
      <div>{{ refusal }}</div>
    </div>
  }
  @if (consultation() && amendment(); as amendment) {
    @let values = amendedValues();
    @let complete = mode() === 'complete';
    <div class="amendment-body consultation">
      <app-embauche-view
        [contact]="values.contact"
        [contract]="values.contract"
        [schedules]="values.schedules"
        [assignment]="values.assignment"
        [documents]="values.documents"
        [previousSchedules]="complete ? (previous()?.schedules ?? null) : null"
        [previousAssignment]="complete ? (previous()?.assignment ?? null) : null"
        (openDocument)="openDocument($event)"
      ></app-embauche-view>
      @if (complete) {
        <app-embauche-tabs-form
          [initWith]="amendedContract() ?? undefined"
          [readOnly]="true"
          [showAmendments]="false"
          [previousSchedules]="previous()?.schedules ?? null"
          [previousAssignment]="previous()?.assignment ?? null"
        ></app-embauche-tabs-form>
      } @else {
        <app-amendment-changes-view [amendment]="amendment"></app-amendment-changes-view>
      }
    </div>
  } @else {
    <div class="amendment-body">
      <app-amendment-summary
        data-tour-anchor="amendment.summary"
        [employee]="employee"
        [contractLabel]="contractLabel"
        [effectiveDate]="payload()?.effectiveDate ?? amendment()?.effectiveDate ?? null"
        [durationLabel]="durationLabel()"
        [reasons]="reasons()"
        [assignmentLabel]="assignmentLabel()"
        [fileName]="fileName()"
      ></app-amendment-summary>

      @if (initial(); as initial) {
        <mat-tab-group class="amendment-tabs">
          <mat-tab>
            <ng-template mat-tab-label>
              <span data-tour-anchor="amendment.amendment-tab">Avenant</span>
            </ng-template>
            <mat-radio-group
              class="duration"
              [formControl]="duration"
              aria-label="Durée de l'avenant"
            >
              <mat-radio-button value="INDETERMINATE">À durée indéterminée</mat-radio-button>
              <mat-radio-button value="TEMPORARY">Temporaire</mat-radio-button>
            </mat-radio-group>
            <div class="dates">
              <mat-form-field>
                <mat-label>Date d'effet</mat-label>
                <input
                  matInput
                  data-tour-anchor="amendment.effective-date"
                  [matDatepicker]="effectivePicker"
                  [formControl]="effectiveDate"
                  required
                />
                <mat-hint>JJ/MM/AAAA</mat-hint>
                <mat-datepicker-toggle
                  matIconPrefix
                  [for]="effectivePicker"
                ></mat-datepicker-toggle>
                <mat-datepicker #effectivePicker></mat-datepicker>
              </mat-form-field>
              @if (duration.value === 'TEMPORARY') {
                <mat-form-field>
                  <mat-label>Date de fin</mat-label>
                  <input matInput [matDatepicker]="endPicker" [formControl]="endDate" required />
                  <mat-hint>JJ/MM/AAAA</mat-hint>
                  <mat-datepicker-toggle matIconPrefix [for]="endPicker"></mat-datepicker-toggle>
                  <mat-datepicker #endPicker></mat-datepicker>
                </mat-form-field>
              }
            </div>
            <fieldset
              class="reasons"
              data-tour-anchor="amendment.reasons"
              [formGroup]="declaredReasons"
            >
              <legend>Objet de l'avenant</legend>
              @for (reason of declarableReasons; track reason.code) {
                <mat-checkbox
                  [formControlName]="reason.code"
                  [attr.data-reason]="reason.code"
                  (change)="onReasonToggled(reason.code, $event.checked)"
                  >{{ reason.label }}</mat-checkbox
                >
              }
            </fieldset>
            <p class="hint">
              Les onglets Horaires et Affectations sont préremplis avec les conditions actuelles du
              contrat, avenants signés compris. Modifiez-y ce qui change, puis cochez le ou les
              motifs correspondants : ils sont vérifiés à l'enregistrement.
            </p>
          </mat-tab>
          <mat-tab>
            <ng-template mat-tab-label>
              <span data-tour-anchor="amendment.schedules-tab">Horaires</span>
            </ng-template>
            <app-schedules-form
              data-tour-anchor="amendment.schedules"
              [initWith]="initialSchedules()"
              [companyId]="contract.company.id"
              [readOnly]="readOnly()"
            ></app-schedules-form>
          </mat-tab>
          <mat-tab label="Affectations">
            <app-assignment-form
              [initWith]="initialAssignment()"
              [readOnly]="readOnly()"
            ></app-assignment-form>
          </mat-tab>
        </mat-tab-group>
      }
    </div>
  }
</mat-dialog-content>
